import { SelectableMediaItem } from '@components/ChooseMediaModal/MobileSelectableMediaList';
import { SummaryBlockVariant } from '@components/ChooseMediaModal/SummaryBlock';
import { getUploadDisabledState } from '@components/ChooseMediaModal/uploadDisabledState';
import { UnfurlItem } from '@lib/external/unfurl';
import { useDebouncedValue } from '@lib/hooks/debounce';
import { useOwnerAffordability } from '@lib/hooks/useOwnerAffordability';
import { useTopUp } from '@lib/hooks/useTopUp';
import {
  filterOutDuplicateIds,
  generateHashesForItems,
  getSelectedData,
  getSelectedUrls,
} from '@lib/utils/chooseMediaUtils';
import {
  estimateIngest,
  formatDurationSummary,
  summarizeDuration,
} from '@lib/utils/ingestEstimate';
import { showToast } from '@lib/utils/toastmaster';
import { computeBillingImpact } from '@stores/Billing';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { trackingStore } from '@stores/Tracking';
import {
  computeMediaStatus,
  computeWarningPercentileDuration,
} from '@stores/UploadURL/mediaStatus';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelectionManagement } from '../../hooks/useSelectionManagement';

// NOTE: This hook covers the multi-step URL upload flow (select → confirm → upload).
// The single-media URL upload case is handled separately by UrlImportComponent
// (src/components/MediaUploadBox/UrlImportComponent.tsx), which has its own
// credit/storage checks but is also part of the upload flow.

export type UrlUploadFlowState = 'idle' | 'selecting' | 'confirming';

interface UseUrlUploadFlowOptions {
  unfurledItems: UnfurlItem[];
  onUploadButtonClick?: (
    selectedVideoUrls: {
      url: string;
      name: string;
      userMetadata?: Record<string, any>;
      uniqueId?: string;
    }[],
    libraryOptions?: {
      createNewLibrary: boolean;
      newLibraryName: string;
      abortController?: AbortController;
    },
  ) => Promise<void> | void;
  createNewLibrary?: boolean;
  /** Name chosen on the upload screen for the new library; when set, the confirm
   *  step shows it read-only instead of collecting its own. */
  newLibraryName?: string;
  onCancel?: () => void;
  uploadProgress?: { completed: number; total: number } | null;
}

export function useUrlUploadFlow({
  unfurledItems,
  onUploadButtonClick,
  createNewLibrary = false,
  newLibraryName,
  onCancel,
  uploadProgress,
}: UseUrlUploadFlowOptions) {
  const [flowState, setFlowState] = useState<UrlUploadFlowState>('idle');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadAbortController, setUploadAbortController] = useState<AbortController | null>(null);

  // ── Hash generation ─────────────────────────────────────────────────
  const [dataWithHashes, setDataWithHashes] = useState<UnfurlItem[]>([]);
  const [isGeneratingHashes, setIsGeneratingHashes] = useState(false);
  const [hashGenerationError, setHashGenerationError] = useState<string | null>(null);

  const debouncedUnfurledItems = useDebouncedValue(unfurledItems, 300);

  const hasHashes = useMemo(() => {
    return (
      debouncedUnfurledItems.length > 0 && debouncedUnfurledItems.every((item) => item.uniqueId)
    );
  }, [debouncedUnfurledItems]);

  useEffect(() => {
    if (hasHashes) {
      setDataWithHashes(debouncedUnfurledItems);
      setIsGeneratingHashes(false);
      setHashGenerationError(null);
      return;
    }

    let cancelled = false;

    const generateHashesForData = async () => {
      try {
        setIsGeneratingHashes(true);
        setHashGenerationError(null);
        const hashedData = await generateHashesForItems(debouncedUnfurledItems);
        if (!cancelled) {
          setDataWithHashes(hashedData);
        }
      } catch (error) {
        console.error('Failed to generate hashes for data:', error);
        if (!cancelled) {
          setHashGenerationError(
            'Failed to process media data. Some features may not work correctly.',
          );
          setDataWithHashes(debouncedUnfurledItems);
        }
      } finally {
        if (!cancelled) {
          setIsGeneratingHashes(false);
        }
      }
    };

    generateHashesForData();

    return () => {
      cancelled = true;
    };
  }, [debouncedUnfurledItems, hasHashes]);

  // ── Duplicate detection ─────────────────────────────────────────────
  const libraryFolder = useLibraryFolder();

  // Snapshot of the library's uniqueId → mediaId map, frozen when the flow
  // opens. Deliberately NOT a live read: `libraryFolder.uniqueIds` mints a new
  // Record every folder poll tick while media is processing, and a live read
  // here subscribes this whole flow (grid rows, columns, mobile list,
  // selection) to that churn — the redraw/scroll-reset bug. Stale badges are
  // safe: handleSubmit in UrlImportComponent re-filters the selection against
  // the LIVE uniqueIds via DuplicateDetectionService at confirm time.
  const [existingUniqueIds, setExistingUniqueIds] = useState<Record<string, string>>({});

  // ── Selection ───────────────────────────────────────────────────────
  const { selectedIds, updateSelection, clearSelection, nonDuplicateSelectedCount } =
    useSelectionManagement({
      data: dataWithHashes,
      existingUniqueIds,
    });

  const [filteredOutCount, setFilteredOutCount] = useState(0);
  const [alreadyInLibraryCount, setAlreadyInLibraryCount] = useState(0);

  // ── Billing ─────────────────────────────────────────────────────────
  const nSelectedVideos = nonDuplicateSelectedCount;
  const ingestEstimate = useMemo(
    () => estimateIngest(getSelectedData(dataWithHashes, selectedIds)),
    [dataWithHashes, selectedIds],
  );

  const { creditCost, creditsLeft, creditsAfterProcessing, storageLeft, storageAfterProcessing } =
    computeBillingImpact(ingestEstimate);

  // The backend charges the library's `billingId`, not the uploader's, so when they differ the
  // figures above describe an account that isn't paying — see `resolveBillingOwnership`. Not the
  // ownership axis: a teammate's library on a shared org billing account is somebody else's and
  // still spends this user's balance. Same `createNewLibrary` carve-out as the file flow: no
  // library yet means nothing to compare.
  const billedToAnotherAccount =
    !createNewLibrary && (libraryFolder.library?.isBilledToAnotherAccount ?? false);

  // Who to name, from the ownership axis — the only one carrying a person. Gated on the flag so
  // it inherits the same `createNewLibrary` carve-out. Kept as its own value rather than derived
  // downstream: an owner the backend gave no name and no email, or a library billed elsewhere but
  // owned by the reader, leaves this undefined while the flag stays true, and the notice has a
  // nameless wording for exactly that case.
  const ownerName = billedToAnotherAccount ? libraryFolder.library?.otherOwnerName : undefined;

  // One probe for the flow, shared by the picker and the confirm screen. The estimate still counts
  // duplicates, which the backend drops at ingest — so this over-estimates, never under, which is
  // the safe direction for a gate that only blocks on a definite no.
  const ownerAffordability = useOwnerAffordability({
    libraryId: libraryFolder.library?.libraryId,
    ingestSeconds: ingestEstimate.durationSeconds,
    enabled: billedToAnotherAccount,
  });

  const isUploadDisabled = getUploadDisabledState(
    creditsAfterProcessing,
    storageAfterProcessing,
    nSelectedVideos,
    isUploading,
    { billedToAnotherAccount, ownerAffordability, ownerName },
  );

  // ── Warning / top-up logic ──────────────────────────────────────────
  // Owner-billed uploads have nothing to top up on this account, so `needsAction` must stay
  // false — it drives the warning CTA and the secondary styling on the Add button.
  const topUp = useTopUp({
    creditCost,
    currentCredits: creditsLeft ?? 0,
    insufficient:
      !billedToAnotherAccount && creditsAfterProcessing !== null && creditsAfterProcessing <= 0,
    source: 'url-upload',
    ...(billedToAnotherAccount
      ? {}
      : {
          storage: {
            afterProcessing: storageAfterProcessing,
            duration: ingestEstimate.durationSeconds,
          },
        }),
  });

  // ── Mobile list ─────────────────────────────────────────────────────
  const mobileListItems: SelectableMediaItem[] = useMemo(() => {
    const percentileDuration = computeWarningPercentileDuration(dataWithHashes);
    return dataWithHashes.map((item) => ({
      id: item.id,
      title: item.title,
      thumbnail: item.thumbnail,
      duration: item.duration,
      status: computeMediaStatus(item, percentileDuration, existingUniqueIds),
    }));
  }, [dataWithHashes, existingUniqueIds]);

  const selectableIds = useMemo(
    () =>
      mobileListItems
        .filter((item) => item.status !== 'in-library' && item.status !== 'error')
        .map((item) => item.id),
    [mobileListItems],
  );

  const handleMobileToggle = useCallback(
    (id: string) => {
      const item = mobileListItems.find((i) => i.id === id);
      if (item?.status === 'in-library' || item?.status === 'error') return;
      const newSelection = selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id];
      updateSelection(newSelection);
    },
    [mobileListItems, selectedIds, updateSelection],
  );

  const handleMobileSelectAll = useCallback(() => {
    updateSelection(selectableIds);
  }, [updateSelection, selectableIds]);

  const handleMobileDeselectAll = useCallback(() => {
    updateSelection([]);
  }, [updateSelection]);

  // ── Flow actions ────────────────────────────────────────────────────
  const open = useCallback(() => {
    // Untracked read (event handler, not render) — spreading copies the
    // content without subscribing to the computed.
    setExistingUniqueIds({ ...libraryFolder.uniqueIds });
    setFlowState('selecting');
  }, [libraryFolder]);

  const close = useCallback(() => {
    setFlowState('idle');
  }, []);

  // Ref so advanceToConfirm always reads the latest values,
  // even when called in the same synchronous block as setSelection.
  const trackingDataRef = useRef({
    nSelectedVideos,
    totalAvailable: dataWithHashes.length,
    filteredOutCount,
    alreadyInLibraryCount,
    durationSeconds: ingestEstimate.durationSeconds,
    creditCost,
    creditsLeft,
  });
  trackingDataRef.current = {
    nSelectedVideos,
    totalAvailable: dataWithHashes.length,
    filteredOutCount,
    alreadyInLibraryCount,
    durationSeconds: ingestEstimate.durationSeconds,
    creditCost,
    creditsLeft,
  };

  const advanceToConfirm = useCallback(() => {
    const t = trackingDataRef.current;
    trackingStore.chooseMediaImported({
      selectedCount: t.nSelectedVideos,
      totalAvailable: t.totalAvailable,
      filteredOutCount: t.filteredOutCount,
      duplicateCount: t.alreadyInLibraryCount,
      totalDurationSeconds: t.durationSeconds,
      creditsCost: t.creditCost,
      creditsAvailable: t.creditsLeft ?? 0,
    });
    setFlowState('confirming');
  }, []);

  const cancelConfirm = useCallback(() => {
    // Only go back to selecting if we're actually in confirming.
    // ChooseMediaModalSummaryConfirm's BaseModal fires onClose on initial mount
    // (useEffect with open=false calls close()), so this guard prevents
    // flowState jumping from idle to selecting on mount.
    if (flowState !== 'confirming') return;
    setFlowState('selecting');
  }, [flowState]);

  const handleUploadUrl = useCallback(
    async ({
      newLibraryName,
      createNewLibrary: shouldCreateNew,
      abortController,
    }: {
      newLibraryName?: string;
      createNewLibrary: boolean;
      abortController: AbortController;
    }) => {
      try {
        setIsUploading(true);
        setUploadAbortController(abortController);

        const selectedVideoUrls = getSelectedUrls(dataWithHashes, selectedIds);

        // Keep flowState as 'confirming' during upload so SummaryConfirm
        // stays mounted showing loading state. Set to idle after completion.
        await onUploadButtonClick?.(selectedVideoUrls, {
          createNewLibrary: shouldCreateNew,
          newLibraryName: newLibraryName || 'My New Library',
          abortController,
        });
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          const isAllBatchesFailedError =
            error.message.startsWith('All ') && error.message.includes('batch(es) failed');
          if (!isAllBatchesFailedError) {
            showToast('library-add-failed', {
              error,
              libraryName: newLibraryName,
              message: error.message || 'An unexpected error occurred',
            });
          }
        }
      } finally {
        setFlowState('idle');
        setIsUploading(false);
        setUploadAbortController(null);
      }
    },
    [dataWithHashes, selectedIds, onUploadButtonClick],
  );

  // ── Summary props ───────────────────────────────────────────────────
  const collectionThumbnail =
    selectedIds.length > 0
      ? dataWithHashes.find((item) => item.id === selectedIds[0])?.thumbnail
      : undefined;

  const summaryData: Array<{ variant: SummaryBlockVariant; value: string | number }> =
    useMemo(() => {
      const duration = summarizeDuration(ingestEstimate);
      return [
        { variant: 'n-selected', value: nSelectedVideos },
        ...(ingestEstimate.imageCount > 0
          ? [{ variant: 'images' as const, value: ingestEstimate.imageCount }]
          : []),
        ...(duration
          ? [{ variant: 'duration' as const, value: formatDurationSummary(duration) }]
          : []),
      ];
    }, [nSelectedVideos, ingestEstimate]);

  // ── Imperative methods for parent ───────────────────────────────────
  const setSelection = useCallback(
    (ids: string[]) => {
      // The "upload all" path enters the flow through here without open(),
      // so the snapshot may be empty/stale. Refresh it and filter against the
      // fresh copy directly — updateSelection's own duplicate filter runs with
      // the previous render's snapshot and can't see this one yet.
      const freshUniqueIds = { ...libraryFolder.uniqueIds };
      setExistingUniqueIds(freshUniqueIds);
      updateSelection(filterOutDuplicateIds(ids, dataWithHashes, freshUniqueIds));
    },
    [updateSelection, libraryFolder, dataWithHashes],
  );

  return {
    flowState,
    open,
    close,

    selectionProps: {
      // Data
      dataWithHashes,
      existingUniqueIds,
      isGeneratingHashes,
      hashGenerationError,
      // Selection
      selectedIds,
      onSelectionChange: updateSelection,
      nonDuplicateSelectedCount,
      // Billing (pre-computed)
      creditCost,
      creditsLeft,
      creditsAfterProcessing,
      storageLeft,
      storageAfterProcessing,
      ingestEstimate,
      billedToAnotherAccount,
      ownerName,
      ownerAffordability,

      // Counts
      nSelected: {
        total: selectedIds.length,
        filteredOut: filteredOutCount,
        alreadyInLibrary: alreadyInLibraryCount,
      },
      onFilteredOutCountChange: setFilteredOutCount,
      onAlreadyInLibraryCountChange: setAlreadyInLibraryCount,
      // Upload state
      isUploadDisabled,
      isUploading,
      // Warning
      hasCreditOrStorageWarning: topUp.needsAction,
      warningActionLabel: topUp.buttonLabel ?? '',
      warningActionTooltip: topUp.tooltip ?? '',
      onWarningAction: topUp.handleTopUp,
      isToppingUp: topUp.isToppingUp,
      // Mobile
      mobileListItems,
      onMobileToggle: handleMobileToggle,
      onMobileSelectAll: handleMobileSelectAll,
      onMobileDeselectAll: handleMobileDeselectAll,
      // Actions
      onUploadButtonClick: advanceToConfirm,
    },

    summaryProps: {
      open: flowState === 'confirming',
      onClose: cancelConfirm,
      onUploadUrl: handleUploadUrl,
      summaryData,
      createNewLibrary,
      newLibraryName,
      collectionThumbnail,
      data: {
        creditCost,
        ingestEstimate,
        storageLeftAfterProcessing: storageAfterProcessing,
        creditsAfterProcessing,
        billedToAnotherAccount,
        ownerName,
        ownerAffordability,
      },
      isUploading,
      uploadProgress: uploadProgress ?? null,
      uploadAbortController,
      onCancel,
    },

    // Imperative methods for parent
    setSelection,
    advanceToConfirm,
  };
}
